import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

export interface BreachPeriod {
  label: string;
  noise: number;
  vibration: number;
  pm10: number;
  pm25: number;
}

export const BREACH_COLORS = {
  noise:     { color: '#5C6BC0', name: 'Noise' },
  vibration: { color: '#6A1B9A', name: 'Vibration' },
  pm10:      { color: '#0288D1', name: 'PM10' },
  pm25:      { color: '#00897B', name: 'PM2.5' },
} as const;

const LAYERS = Object.entries(BREACH_COLORS) as [keyof typeof BREACH_COLORS, { color: string; name: string }][];

interface Props {
  data: BreachPeriod[];
  height?: number;
}

export default function MiniStackedBarChart({ data, height = 70 }: Props) {
  if (!data.length) return null;

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fallbackText}>
          {data.map(d => `${d.label}:${d.noise + d.vibration + d.pm10 + d.pm25}`).join('  ')}
        </Text>
      </View>
    );
  }

  const maxTotal = Math.max(...data.map(d => d.noise + d.vibration + d.pm10 + d.pm25), 1);
  const BAR_W  = 22;
  const GAP    = 7;
  const LABEL_H = 14;
  const svgW = data.length * (BAR_W + GAP) - GAP + 8;
  const svgH = height + LABEL_H;

  return (
    <View>
      {/* @ts-ignore — SVG renders fine on RN Web */}
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        {data.map((d, i) => {
          const x = 4 + i * (BAR_W + GAP);
          let yOffset = height;
          const segments = LAYERS.map(([key, meta]) => {
            const val = d[key];
            const segH = (val / maxTotal) * height;
            yOffset -= segH;
            return { key, color: meta.color, y: yOffset, segH, val };
          });
          const total = d.noise + d.vibration + d.pm10 + d.pm25;
          const topY  = height - (total / maxTotal) * height;
          return (
            <g key={i}>
              {segments.map(seg => seg.val > 0 ? (
                // @ts-ignore
                <rect key={seg.key} x={x} y={seg.y} width={BAR_W} height={seg.segH}
                  fill={seg.color} opacity={0.88} rx={2} />
              ) : null)}
              {total > 0 && (
                // @ts-ignore
                <text x={x + BAR_W / 2} y={topY - 2} textAnchor="middle"
                  fontSize={8} fill="#555" fontWeight="600">{total}</text>
              )}
              {/* @ts-ignore */}
              <text x={x + BAR_W / 2} y={height + LABEL_H} textAnchor="middle"
                fontSize={8} fill="#999">{d.label}</text>
            </g>
          );
        })}
      </svg>

      <View style={styles.legend}>
        {LAYERS.map(([key, meta]) => (
          <View key={key} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: meta.color }]} />
            <Text style={styles.legendText}>{meta.name}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  fallback:     { paddingVertical: 4 },
  fallbackText: { fontSize: 10, color: '#999' },
  legend:       { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  legendItem:   { flexDirection: 'row', alignItems: 'center', gap: 3 },
  legendDot:    { width: 8, height: 8, borderRadius: 2 },
  legendText:   { fontSize: 9, color: '#666' },
});
